/**
 * THE-ACTIVE-BUSINESS-IS-NOT-FLIPPED-ON-LOAD-1 — which business a person is in when
 * the app loads, and who writes it down.
 *
 * THE SERVER ROW IS THE TRUTH. The load used to copy this browser's remembered business
 * up to `user_active_membership`, so a stale memory flipped the owner into an empty
 * business while the planner, which reads the server, refused for it. Now the server's
 * row wins and the browser's memory is set to match it, never the reverse; only an
 * explicit switch writes the browser's choice to the server.
 *
 * Three answers from the server, never alike: a row (it wins), no row (the default is
 * written once, so the planner can see it), and "could not ask" (today's browser rule,
 * and nothing is written, because a guess is not a choice).
 *
 * ONE EXCEPTION, kept from SHELF-FLASH-2: an account created after the row was last
 * written wins and is written. Creating a business reloads before its membership is in
 * the list, so the switch it makes cannot land; without this the person would arrive
 * in the business they just left.
 */

/**
 * WHICH ACCOUNT A PERSON LANDS IN.
 *
 * BEFORE: `validStored ?? fallback` — a stored id won whenever it was still a
 * membership, so an account created seconds ago lost to one chosen weeks ago.
 *
 * AFTER: a stored id wins only when it is a current membership AND no account
 * was created after the choice was stored. Anything newer than the choice means
 * the person has been somewhere since, and the newest account is where they
 * just were.
 *
 * Pure so a guard can drive it without a browser.
 */
export function resolveActiveSubAccount(args: {
  storedId: string | null
  storedAt: number
  memberships: ReadonlyArray<{ sub_account_id: string | null }>
  subs: ReadonlyArray<{ id: string; is_default?: boolean | null; created_at?: string | null }>
}): string | null {
  const { storedId, storedAt, memberships, subs } = args
  const isMember = Boolean(storedId) && memberships.some((m) => m.sub_account_id === storedId)
  // A CHOICE OLDER THAN AN ACCOUNT LOSES TO IT. `storedAt` of 0 - no stamp, or
  // an unreadable one - loses to every account that has a creation time, which
  // is every account.
  const newest = newestOf(subs)
  const supersededBy = newest !== null && newest.t > storedAt ? newest.id : null
  const validStored = isMember && supersededBy === null ? storedId : null
  // The fallback prefers the account that superseded the choice, then the
  // default, then the first. Nobody picked any of them, so none is stored.
  const fallback = supersededBy ?? subs.find((s) => s.is_default)?.id ?? subs[0]?.id ?? null
  return validStored ?? fallback
}

/** An unreadable creation time counts as the beginning of time, so it outranks nothing. */
function created(s: { created_at?: string | null }): number {
  const t = Date.parse(String(s.created_at))
  return Number.isFinite(t) ? t : 0
}

/** The most recently created business; on a tie, the one listed first. */
function newestOf(
  subs: ReadonlyArray<{ id: string; created_at?: string | null }>,
): { id: string; t: number } | null {
  return subs.reduce<{ id: string; t: number } | null>((best, s) => {
    const t = created(s)
    return best === null || t > best.t ? { id: s.id, t } : best
  }, null)
}

export type ServerChoice =
  | { state: 'found'; teamMemberId: string; updatedAt: number }
  | { state: 'none' }
  | { state: 'failed' }

export interface OnLoad {
  /** The business the screen, the switcher and the panel read. */
  activeId: string | null
  /** A membership to write to the server once, or null to write nothing. */
  write: string | null
  /** What this browser remembers afterwards, or null to leave it alone. */
  remember: string | null
  /** The remembered business is one the person no longer belongs to. */
  forget: boolean
}

export function activeOnLoad(args: {
  server: ServerChoice
  storedId: string | null
  storedAt: number
  memberships: ReadonlyArray<{ id: string; sub_account_id: string | null }>
  subs: ReadonlyArray<{ id: string; is_default?: boolean | null; created_at?: string | null }>
}): OnLoad {
  const { server, storedId, storedAt, memberships, subs } = args
  const membershipFor = (subId: string | null) =>
    memberships.find((m) => m.sub_account_id === subId) ?? null
  const forget = storedId !== null && membershipFor(storedId) === null

  if (server.state === 'failed') {
    // The browser rule already refuses a business the person is not a member of.
    const activeId = resolveActiveSubAccount({ storedId, storedAt, memberships, subs })
    return { activeId, write: null, remember: null, forget }
  }

  if (server.state === 'found') {
    const row = memberships.find((m) => m.id === server.teamMemberId)
    if (row) {
      const newest = newestOf(subs)
      const newer = newest !== null && newest.t > server.updatedAt ? newest.id : null
      const pick = newer ?? row.sub_account_id
      const write = pick === row.sub_account_id ? null : (membershipFor(pick)?.id ?? null)
      return { activeId: pick, write, remember: pick, forget }
    }
  }
  // No row, or one naming a membership the person no longer has.
  const pick = subs.find((s) => s.is_default)?.id ?? subs[0]?.id ?? null
  return { activeId: pick, write: membershipFor(pick)?.id ?? null, remember: pick, forget }
}
